import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPolicyPage = () => {
  const lastUpdated = "February 16, 2026";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-primary-foreground font-bold text-sm">H</span>
              </div>
              <span className="font-serif text-xl font-medium text-primary">HomeClass</span>
            </Link>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary">
                Privacy Policy
              </h1>
              <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none" data-testid="privacy-content">
            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                HomeClass ("we," "our," or "us") is committed to protecting the privacy of teachers, parents, and students who use our AI-powered quiz generation platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
              </p>
              <p className="text-muted-foreground">
                By using HomeClass, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">2. Information We Collect</h2>
              
              <h3 className="font-medium text-foreground mb-2">2.1 Account Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-4">
                <li>Email address</li>
                <li>Name</li>
                <li>Password (encrypted)</li>
                <li>Subscription and billing information</li>
              </ul>

              <h3 className="font-medium text-foreground mb-2">2.2 Student Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-4">
                <li>First name only (we do not collect full names of students)</li>
                <li>4-digit PIN for login (not a password)</li>
                <li>Quiz responses and scores</li>
              </ul>

              <h3 className="font-medium text-foreground mb-2">2.3 Educational Content</h3>
              <ul className="list-disc pl-6 text-muted-foreground mb-4">
                <li>Lesson content you upload or paste</li>
                <li>Generated quizzes and questions</li>
                <li>Quiz assignments and results</li>
              </ul>

              <h3 className="font-medium text-foreground mb-2">2.4 Usage Information</h3>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Log data (IP address, browser type, pages visited)</li>
                <li>Device information</li>
                <li>Feature usage statistics</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Provide and maintain our service</li>
                <li>Generate AI-powered quizzes from your lesson content</li>
                <li>Process quiz submissions and calculate scores</li>
                <li>Send email notifications about quiz assignments</li>
                <li>Process payments and manage subscriptions</li>
                <li>Improve and personalize our service</li>
                <li>Communicate with you about updates and support</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">4. Children's Privacy (COPPA Compliance)</h2>
              <p className="text-muted-foreground mb-4">
                HomeClass is designed for use by teachers and parents to create educational content for students. We take children's privacy seriously:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>We collect only the minimum information necessary (first name and PIN) for student accounts</li>
                <li>Student accounts are created and managed by teachers/parents, not by children directly</li>
                <li>We do not collect email addresses or other personal contact information from students</li>
                <li>We do not display advertising to students</li>
                <li>Parents/teachers can review and delete student information at any time</li>
                <li>We do not share student data with third parties for marketing purposes</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-muted-foreground mb-4">We do not sell your personal information. We may share information with:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li><strong>Service Providers:</strong> Third-party services that help us operate our platform (e.g., payment processing via Stripe, email delivery via Resend, AI processing via OpenAI)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">6. Data Security</h2>
              <p className="text-muted-foreground mb-4">
                We implement appropriate technical and organizational measures to protect your data:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Secure password hashing</li>
                <li>Regular security assessments</li>
                <li>Access controls and authentication</li>
                <li>Secure cloud infrastructure</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">7. Data Retention</h2>
              <p className="text-muted-foreground">
                We retain your data for as long as your account is active or as needed to provide you services. You can request deletion of your account and associated data at any time by contacting us. We may retain certain information as required by law or for legitimate business purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">8. Your Rights</h2>
              <p className="text-muted-foreground mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and data</li>
                <li>Export your data</li>
                <li>Opt out of marketing communications</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">9. Cookies and Tracking</h2>
              <p className="text-muted-foreground">
                We use essential cookies to maintain your session and preferences. We do not use third-party advertising cookies. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">10. Changes to This Policy</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-serif text-xl font-medium text-primary mb-4">11. Contact Us</h2>
              <p className="text-muted-foreground">
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-secondary/50 rounded-lg">
                <p className="text-foreground font-medium">HomeClass Support</p>
                <p className="text-muted-foreground">
                  Email:{' '}
                  <a 
                    href="mailto:homeclassem@gmail.com" 
                    className="text-primary hover:underline"
                  >
                    homeclassem@gmail.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center justify-between w-full gap-4">
              <p className="text-sm text-muted-foreground">
                © 2026 HomeClass. All rights reserved.
              </p>
              <div className="flex gap-6">
                <Link to="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-sm text-primary font-medium">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              For support, contact us at{' '}
              <a 
                href="mailto:homeclassem@gmail.com" 
                className="text-primary hover:underline"
              >
                homeclassem@gmail.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
